import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DeviceApplicationStateService } from '@rocksoft/appmanagement-sdk';
import {
  AppleDevice,
  DeviceOperationsService,
  DevicesService,
  IntegrationMDMDevicesService,
  IntegrationMDMProxyService,
  DeviceDto as LocationsDevice,
} from '@rocksoft/locations-sdk';
import { DevicesService as MdmDevicesService } from '@rocksoft/mdm-sdk-v2';
import { DeviceService as OsUpdatesDeviceService } from '@rocksoft/osupdates-sdk';
import { SettingsDeviceService } from '@rocksoft/settings-sdk';
import { map, mergeMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { safeMap } from '../effects.helpers';
import { DevicesRouterStoreSelectors, LocationsRouterStoreSelectors } from '../router';
import {
  BrandViewActions,
  CategorySettingsWallpaperViewActions,
  DeviceActionsActions,
  DeviceApiActions,
  DeviceApiWallpaperActions,
  DeviceAssignmentViewActions,
  DeviceDetailsContextMenuActions,
  DeviceModalActions,
  DeviceResolverActions,
  DeviceSyncStatusViewActions,
  DevicesSyncModalActions,
  GatewayApiActions,
  LocationViewActions,
} from './device.actions';

import { ExportService } from './export.service';

import { SyncType } from '@rocksoft/apple-sync-sdk';
import { DeviceTaskService, TaskType } from '@rocksoft/device-task-management-sdk';
import { Device } from '@rocksoft/shared';
import { getDevicesFilteredOnSubset } from './utils/device.utils';

function toDeviceTaskType(syncTypes: SyncType[]): TaskType[] | undefined {
  const taskTypes: TaskType[] = [];
  for (const syncType of syncTypes) {
    switch (syncType) {
      case SyncType.Applications:
        taskTypes.push(TaskType.ApplicationSync);
        break;
      case SyncType.NotificationProfiles:
        taskTypes.push(TaskType.NotificationSync);
        break;
      case SyncType.WifiProfiles:
        taskTypes.push(TaskType.WifiSync);
        break;
      case SyncType.EraApplicationConfiguration:
        taskTypes.push(TaskType.ApplicationConfigurationsSync);
        break;
      case SyncType.AppLockProfile:
        taskTypes.push(TaskType.AppLockSync);
        break;
      case SyncType.RestrictionsProfiles:
        taskTypes.push(TaskType.RestrictionsSync);
        break;
      case SyncType.DeviceName:
        taskTypes.push(TaskType.DeviceNameSync);
        break;
      case SyncType.AirplaySecurityProfile:
        taskTypes.push(TaskType.AirplaySync);
        break;
      case SyncType.ConfigurationProfiles:
        taskTypes.push(TaskType.ConfigurationSync);
        break;
      default:
        return undefined;
    }
    return taskTypes;
  }
}

@Injectable()
export class DeviceEffects {
  private readonly deviceService = inject(DevicesService);
  private readonly exportService = inject(ExportService);
  private readonly integrationMDMProxyService = inject(IntegrationMDMProxyService);
  private readonly appleDeviceService = inject(IntegrationMDMDevicesService);
  private readonly deviceApplicationStateService = inject(DeviceApplicationStateService);
  private readonly mdmDevicesService = inject(MdmDevicesService);
  private readonly actions$ = inject(Actions);

  private readonly settingsDeviceService = inject(SettingsDeviceService);
  private readonly deviceOperationsService = inject(DeviceOperationsService);
  private readonly osUpdatesDeviceService = inject(OsUpdatesDeviceService);
  private readonly deviceTaskService = inject(DeviceTaskService);
  private readonly store = inject(Store);

  // ============================================================================
  // RESOLVER EFFECTS - Basic Device Info
  // ============================================================================

  devicesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesRequested),
      mergeMap(({ categoryId }) =>
        safeMap(
          this.deviceService.getCategoryDevices({ categoryId }),
          (devices: LocationsDevice[]) => DeviceApiActions.basicDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.basicDeviceInfoLoadError({ error })
        )
      )
    );
  });

  devicesForStateLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.devicesForStateRequested),
      mergeMap(({ state }) =>
        safeMap(
          this.deviceService.getDevicesByState({ state }),
          (devices: LocationsDevice[]) => DeviceApiActions.basicDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.basicDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - Apple Device Info
  // ============================================================================

  appleDeviceLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.appleDeviceRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.appleDeviceService.getAppleDevice({ udid }),
          (device: AppleDevice) => DeviceApiActions.appleDeviceInfoLoaded({ appleDevice: { ...device, udid } }),
          (error) => DeviceApiActions.appleDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - Application State
  // ============================================================================

  deviceApplicationStatesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.deviceApplicationStatesRequested),
      mergeMap(() =>
        safeMap(
          this.deviceApplicationStateService.getAllDeviceStates(),
          (states) => DeviceApiActions.applicationStateLoaded({ states }),
          (error) => DeviceApiActions.applicationStateLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESOLVER EFFECTS - MDM Devices
  // ============================================================================

  mdmDevicesLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.mdmDevicesRequested),
      mergeMap(() =>
        safeMap(
          this.mdmDevicesService.getDevices({ apiVersion: '2.0' }),
          (devices) => DeviceApiActions.mdmDeviceInfoLoaded({ devices }),
          (error) => DeviceApiActions.mdmDeviceInfoLoadError({ error })
        )
      )
    );
  });

  mdmDeviceLoadedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceResolverActions.mdmDeviceRequested),
      mergeMap(({ udid }) =>
        safeMap(
          this.mdmDevicesService.getDevice({ deviceType: 'Apple', id: udid, apiVersion: '2.0' }),
          (device) => DeviceApiActions.mdmDeviceInfoLoaded({ devices: [device] }),
          (error) => DeviceApiActions.mdmDeviceInfoLoadError({ error })
        )
      )
    );
  });

  // ============================================================================
  // DEVICE ASSIGNMENT EFFECTS
  // ============================================================================

  categoryAssignedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceAssignmentViewActions.categoryAssigned),
      mergeMap(({ deviceId, categoryId }) =>
        safeMap(
          this.settingsDeviceService.assignDeviceToCategory({ deviceId, assignDeviceCategoryDto: { categoryId } }).pipe(
            mergeMap(() =>
              categoryId === undefined
                ? this.deviceService.getDevicesByState({ state: 'Unavailable' })
                : this.deviceService.getCategoryDevices({ categoryId })
            ),
            map((devices) => devices.find((candidate) => candidate.deviceId === deviceId))
          ),
          (device) => DeviceApiActions.categoryAssignmentSucceeded({ device }),
          (error) => DeviceApiActions.categoryAssignmentFailed({ deviceId, error }),
          'The device could not be assigned to the category.'
        )
      )
    );
  });

  // ============================================================================
  // SYNC EFFECTS
  // ============================================================================

  deviceSyncRetryEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retrySync),
      mergeMap(({ udid, forceSync }) =>
        safeMap(
          this.deviceTaskService.startSync({
            deviceTasksScheduleRequest: {
              deviceUdids: [udid ?? ''],
              force: forceSync,
            },
          }),
          () => DeviceApiActions.syncRetried({ udid }),
          (error) => DeviceApiActions.deviceSyncRetryError({ error })
        )
      )
    );
  });

  deviceRetryGivenSyncEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryGivenSync),
      withLatestFrom(this.store.select(DevicesRouterStoreSelectors.selectUdid)),
      mergeMap(([{ syncType, forceSync }, udid]) =>
        safeMap(
          this.deviceTaskService.startSync({
            deviceTasksScheduleRequest: {
              deviceUdids: [udid ?? ''],
              taskTypes: toDeviceTaskType([syncType]),
              force: forceSync,
            },
          }),
          () => DeviceApiActions.givenSyncRetried({ udid: udid ?? '', syncType }),
          (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
        )
      )
    );
  });

  devicesSyncRequestedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DevicesSyncModalActions.syncDevicesRequested),
      mergeMap(({ locationSubset, force, syncTypes }) =>
        getDevicesFilteredOnSubset(this.store, locationSubset).pipe(
          take(1),
          map((devices: Device[]) => devices.map((device) => device.udid)),
          mergeMap((deviceIds: string[]) =>
            safeMap(
              this.deviceTaskService.startSync({
                deviceTasksScheduleRequest: {
                  deviceUdids: deviceIds,
                  taskTypes: toDeviceTaskType(syncTypes),
                  force: force,
                },
              }),
              () => DeviceApiActions.syncDevicesRequestSuccessful({ udids: deviceIds, syncTypes }),
              (error) => DeviceApiActions.deviceGivenSyncRetryError({ error })
            )
          )
        )
      )
    );
  });

  // ============================================================================
  // OS UPDATE EFFECTS
  // ============================================================================

  deviceOsUpdateResetEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.retryOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.resetOsUpdateState({ udid }),
          () => DeviceApiActions.oSUpdateRetried({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateRetryError({ error })
        )
      )
    );
  });

  deviceOsUpdateContinueEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceSyncStatusViewActions.continueOsUpdate),
      mergeMap(({ udid }) =>
        safeMap(
          this.osUpdatesDeviceService.scheduleOsUpdate({ udid }),
          () => DeviceApiActions.oSUpdateContinued({ udid }),
          (error) => DeviceApiActions.deviceOsUpdateContinueError({ error })
        )
      )
    );
  });

  // ============================================================================
  // LOST MODE EFFECTS
  // ============================================================================

  deviceLostModeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceLostModeCommandSent),
      mergeMap(({ udid, enableLostModeCommand }) =>
        safeMap(
          this.integrationMDMProxyService.queueEnableLostModeCommand({ udid, enableLostModeCommand }),
          (commandProgress) => DeviceApiActions.devicePutInLostMode({ udid: commandProgress.udid ?? '' }),
          (error) => DeviceApiActions.failedToPutDeviceIntoLostMode({ error })
        )
      )
    );
  });

  deviceDisableLostModeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceDisableLostModeCommandSent),
      mergeMap(({ udid }) =>
        safeMap(
          this.integrationMDMProxyService.queueDisableLostModeCommand({ udid }),
          (commandProgress) => DeviceApiActions.deviceTakenOutOfLostMode({ udid: commandProgress.udid ?? '' }),
          (error) => DeviceApiActions.failedToTakeDeviceOutOfLostMode({ error })
        )
      )
    );
  });

  // ============================================================================
  // RESTART/REBOOT EFFECTS
  // ============================================================================

  deviceRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceDetailsContextMenuActions.deviceRestartCommandSent),
      mergeMap(({ udid }) =>
        safeMap(
          this.integrationMDMProxyService.queueRestartDeviceCommand({ udid }),
          () => DeviceApiActions.deviceRestartSuccessful(),
          (error) => DeviceApiActions.failedToRestartDevice({ error })
        )
      )
    );
  });

  devicesRebootEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceModalActions.rebootDevicesRequested),
      mergeMap(({ locationSubset, modelNames }) =>
        getDevicesFilteredOnSubset(this.store, locationSubset).pipe(
          take(1),
          map((devices: Device[]) =>
            devices.filter((device) =>
              [...modelNames].some((modelName) => [...(device?.apple?.modelName ?? [])].includes(modelName))
            )
          ),
          map((devices: Device[]) => devices.map((device) => device.udid)),
          mergeMap((deviceIds: string[]) =>
            safeMap(
              this.deviceOperationsService.queueRestartDevicesCommand({
                devicesQuery: { selector: { devices: deviceIds } },
              }),
              () => DeviceApiActions.restartDevicesRequestSuccessful(),
              (error) => DeviceApiActions.restartDevicesError({ error })
            )
          )
        )
      )
    );
  });

  // ============================================================================
  // WALLPAPER EFFECTS
  // ============================================================================

  deviceWallpaperEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CategorySettingsWallpaperViewActions.wallpaperCommandSent),
      mergeMap(({ wallpaper, categoryId }) =>
        safeMap(
          this.integrationMDMProxyService.queueSetWallpaperByCategoryCommand({
            image: wallpaper.image,
            where: wallpaper.where,
            categoryId,
          }),
          () => DeviceApiWallpaperActions.wallpaperSuccessfullySet(),
          (error) => DeviceApiWallpaperActions.wallpaperFailedToSet({ error })
        )
      )
    );
  });

  // ============================================================================
  // DEVICE NAME EFFECTS
  // ============================================================================

  deviceSetNameEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DeviceActionsActions.updateDeviceNameOverride),
      mergeMap(({ deviceId, newName }) =>
        safeMap(
          this.deviceService.updateDeviceNameOverride({
            deviceId,
            updateDeviceNameOverrideDto: { deviceNameOverride: newName },
          }),
          (device) => DeviceApiActions.updateDeviceNameOverrideSucceeded({ device }),
          (error) => DeviceApiActions.updateDeviceNameOverrideFailed({ error })
        )
      )
    );
  });

  // ============================================================================
  // EXPORT EFFECTS
  // ============================================================================

  exportLocationDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(LocationViewActions.exportDevices),
      withLatestFrom(
        this.store.select(LocationsRouterStoreSelectors.selectBrandId),
        this.store.select(LocationsRouterStoreSelectors.selectLocationHumanId)
      ),
      mergeMap(([, brandId, locationId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, locationId ?? undefined, 'body').pipe(
            tap((res: Blob) => {
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });

  exportBrandDevicesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BrandViewActions.exportDevices),
      withLatestFrom(this.store.select(LocationsRouterStoreSelectors.selectBrandId)),
      mergeMap(([, brandId]) =>
        safeMap(
          this.exportService.getDeviceDetails(brandId, undefined, 'body').pipe(
            tap((res: Blob) => {
              const blob = new Blob([res], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });
              const uri = window.URL.createObjectURL(blob);
              window.open(uri, 'download');
            })
          ),
          () => GatewayApiActions.exportDevicesSuccess(),
          (error) => GatewayApiActions.exportDevicesFailed({ error })
        )
      )
    );
  });
}
